import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Lock } from "lucide-react";
import { BioButton } from "@/components/bio/BioButton";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function SettingsView() {
    const { user } = useAuth();
    const canEdit = user?.role === "super_admin";
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/settings").then(({ data }) => setS(data));
    }, []);

    const set = (k) => (e) => setS((x) => ({ ...x, [k]: e.target?.value ?? e }));

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/settings", {
                default_daily_limit: Number(s.default_daily_limit),
                kiosk_timeout: Number(s.kiosk_timeout),
                default_language: s.default_language,
                consent_text: s.consent_text,
            });
            toast.success("Configuración guardada");
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-5 max-w-2xl">
            <div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Configuración</h1>
                <p className="text-bio-text-secondary mt-1 text-sm">Parámetros globales de la plataforma.</p>
            </div>
            {!s ? <Skeleton className="h-80 rounded-bio-card" /> : (
                <div className="bg-white rounded-bio-card border border-bio-border-subtle shadow-bio-sm p-6 space-y-5">
                    {!canEdit && <div className="flex items-center gap-2 text-sm text-bio-text-secondary bg-bio-bg rounded-bio px-3 py-2"><Lock className="h-4 w-4" /> Solo el super administrador puede editar.</div>}
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5"><Label className="text-sm font-semibold">Moneda</Label><Input value={s.currency} disabled className="h-11 rounded-bio border-[1.5px]" /></div>
                        <div className="space-y-1.5"><Label className="text-sm font-semibold">Formato de fecha</Label><Input value={s.date_format} disabled className="h-11 rounded-bio border-[1.5px]" /></div>
                        <div className="space-y-1.5">
                            <Label className="text-sm font-semibold">Idioma por defecto</Label>
                            <Select value={s.default_language} onValueChange={(v) => canEdit && setS((x) => ({ ...x, default_language: v }))}>
                                <SelectTrigger data-testid="settings-lang" disabled={!canEdit} className="h-11 rounded-bio border-[1.5px]"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="es">Español</SelectItem><SelectItem value="en">Inglés</SelectItem></SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5"><Label className="text-sm font-semibold">Límite diario por defecto (USD)</Label><Input data-testid="settings-limit" type="number" min="0" step="0.5" value={s.default_daily_limit} onChange={set("default_daily_limit")} disabled={!canEdit} className="h-11 rounded-bio border-[1.5px]" /></div>
                        <div className="space-y-1.5"><Label className="text-sm font-semibold">Tiempo de inactividad kiosco (seg)</Label><Input data-testid="settings-timeout" type="number" min="10" value={s.kiosk_timeout} onChange={set("kiosk_timeout")} disabled={!canEdit} className="h-11 rounded-bio border-[1.5px]" /></div>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-sm font-semibold">Texto de consentimiento biométrico del acudiente</Label>
                        <textarea data-testid="settings-consent" value={s.consent_text} onChange={set("consent_text")} disabled={!canEdit} rows={4} className="w-full rounded-bio border-[1.5px] border-bio-border bg-bio-bg p-3 text-sm focus:outline-none focus-visible:ring-4 focus-visible:ring-[#2A87C8]/20 disabled:opacity-70" />
                    </div>
                    {canEdit && <BioButton data-testid="settings-save" loading={saving} onClick={save}><Save className="h-4 w-4" /> Guardar configuración</BioButton>}
                </div>
            )}
        </div>
    );
}
